import { Alert, Form } from "antd";
import { BaseModalRef } from "components/BaseModal";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _, { isArray } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, urlRegexValidator } from "utils/formUtils";

import AutoMappingSettingsForm, {
  GIT_TOOL_MAPPING_CONFIG,
} from "../common/AutoMappingSettingsForm";

interface GithubPATConfigFormProps {
  installationId?: string;
  afterClose?: () => void;
  unlinkInstallation?: boolean;
  toolName?: string;
}

interface GithubPATConfigState {
  hostUrl?: string;
  token?: string;
  organisation?: string;
}

type ToolConfigs = Record<string, boolean>;

const GithubPATConfigForm = forwardRef<BaseModalRef, GithubPATConfigFormProps>(
  ({ installationId, toolName, ...props }, ref) => {
    const toolConfigs = useRef<ToolConfigs | null>(null);
    const featureFlags = useFeatureFlag();
    const [form] = Form.useForm();
    const [data, setData] = useState<GithubPATConfigState>({});

    const { hostUrl, token } = data;

    const githubOrgParams = useMemo(
      () => ({ hostUrl, token }),
      [hostUrl, token],
    );

    const fetchOrganisations = useCallback((params: any) => {
      let timerId: NodeJS.Timeout | null = null;
      let apiCancel: (() => void) | null = null;
      const promise = new Promise((resolve, reject) => {
        timerId = setTimeout(() => {
          const { promise: apiPromise, cancel } =
            ToolsIntegrationsAPI.postGithubOrganisations(params);
          apiCancel = cancel;
          apiPromise.then(resolve).catch(reject);
        }, 1000);
      });

      const cancel = () => {
        if (timerId) {
          clearTimeout(timerId);
        }
        if (apiCancel) {
          apiCancel();
        }
      };

      return { promise, cancel };
    }, []);

    const submitData = useCallback(
      async (values: GithubPATConfigState) => {
        const configs = toolConfigs.current;
        const organisations = isArray(values.organisation)
          ? values.organisation
          : [values.organisation];

        const payloads = organisations.map((org) => ({
          ...values,
          repoType: "GITHUB",
          account: org,
          installationConfig: {
            authType: "TOKEN",
            subProductMappingConfig: featureFlags.SIMPLIFIED_ONBOARDING
              ? _.filter(
                  [
                    GIT_TOOL_MAPPING_CONFIG.MAP_INACTIVE_REPOS,
                    GIT_TOOL_MAPPING_CONFIG.MAP_DORMANT_REPOS,
                  ],
                  (entry) => configs?.[entry],
                )
              : [],
            codeClassificationEnabled: configs?.codeClassificationEnabled,
            githubIssuesEnabled: configs?.githubIssuesEnabled,
          },
        }));

        await ToolsIntegrationsAPI.createGitInstallationsBulk(payloads).promise;

        message.success(`Successfully configured.`);
      },
      [featureFlags.SIMPLIFIED_ONBOARDING],
    );

    const onSettingsChange = useCallback(
      (changedSetting: unknown, settings: ToolConfigs) => {
        toolConfigs.current = settings;
      },
      [],
    );

    const GithubPATFields = useMemo(
      () => [
        {
          name: "hostUrl",
          label: "Host Url",
          rules: [urlRegexValidator()],
          colSpan: 24,
          required: true,
        },
        {
          name: "token",
          label: "Token",
          type: FieldTypes.PASSWORD,
          help: "Enter a valid token or Generate it",
          colSpan: 24,
          required: true,
        },
        {
          name: "organisation",
          label: "Organisation",
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          colSpan: 24,
          required: true,
          props: form.getFieldValue("hostUrl") &&
            form.getFieldValue("token") && {
              mode: "multiple",
              api: fetchOrganisations,
              apiParams: githubOrgParams,
              showDefaultError: false,
            },
        },
      ],
      [form, githubOrgParams, fetchOrganisations],
    );

    const onValuesChange = useCallback(
      (_: unknown, values: GithubPATConfigState) => {
        setData((prevData) => ({ ...prevData, ...values }));
      },
      [],
    );

    return (
      <BaseModalForm
        ref={ref}
        title="Configure Github using Personal Access Token"
        afterClose={props.afterClose}
        form={form}
        onOk={submitData}
        fields={GithubPATFields}
        onValuesChange={onValuesChange}
        initialValues={{ hostUrl: "https://github.com" }}
      >
        {props.unlinkInstallation && (
          <Alert
            type="warning"
            className="m-b-sm"
            message="This will also remove the original installation"
          />
        )}

        {!installationId && (
          <AutoMappingSettingsForm
            onChange={onSettingsChange}
            toolName={toolName}
          />
        )}
      </BaseModalForm>
    );
  },
);

export default GithubPATConfigForm;
